import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X, ArrowUpRight, Trash2 } from "lucide-react";

export default function AdminQuizzes() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [merchants, setMerchants] = useState([]);

  const load = () => api.get("/quizzes").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
    api.get("/merchants").then((r) => setMerchants(r.data)).catch(() => {});
  }, []);

  const remove = async (id) => {
    try { await api.delete(`/quizzes/${id}`); toast.success("Quiz clos."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-12">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="phenix-eyebrow mb-3">Récompenses</div>
          <h1 className="heading-display text-2xl lg:text-3xl text-plum-900">
            Quiz <em className="text-pink-500">& jeux.</em>
          </h1>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="btn-pill bg-plum-600 hover:bg-plum-700 text-white h-12 px-6 group"
          data-testid="admin-quiz-create-btn">
          <Plus size={14} className="mr-2" /> Nouveau quiz
          <ArrowUpRight size={14} className="ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Button>
      </header>

      {items.length === 0 ? (
        <div className="border border-dashed border-cream-200 p-16 text-center text-plum-700/50 italic">
          Aucun quiz actif.
        </div>
      ) : (
        <div className="divide-y divide-cream-200 border-y border-cream-200">
          {items.map((q, i) => (
            <div key={q.id} className="grid lg:grid-cols-12 gap-6 py-8">
              <div className="lg:col-span-1 font-mono text-[11px] text-plum-700/50">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="lg:col-span-2">
                {q.merchant_name && (
                  <div className="phenix-eyebrow text-pink-500 mb-1.5">chez {q.merchant_name}</div>
                )}
                <div className="text-[11px] text-plum-700/50 italic">{q.questions.length} questions</div>
              </div>
              <div className="lg:col-span-7">
                <h3 className="heading-display text-base text-plum-900 mb-2">{q.title}</h3>
                <p className="text-sm text-plum-700 leading-relaxed mb-2">{q.description}</p>
                <div className="text-sm text-plum-900">
                  <span className="italic text-plum-700/70">Récompense :</span> <strong>{q.reward_label}</strong>
                </div>
              </div>
              <div className="lg:col-span-2 flex flex-col items-end">
                <button onClick={() => remove(q.id)} className="text-[11px] uppercase tracking-widest text-plum-700/50 hover:text-pink-500 transition-colors">
                  <Trash2 size={12} className="inline mr-1" /> Clore
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateQuizDialog open={creating} merchants={merchants} onClose={() => setCreating(false)} onCreated={load} />
    </div>
  );
}

function CreateQuizDialog({ open, onClose, onCreated, merchants }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [merchantId, setMerchantId] = useState("none");
  const [rewardLabel, setRewardLabel] = useState("");
  const [rewardPercent, setRewardPercent] = useState(10);
  const [questions, setQuestions] = useState([{ question: "", options: ["", "", "", ""], correct_index: 0 }]);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setTitle(""); setDescription(""); setMerchantId("none"); setRewardLabel(""); setRewardPercent(10);
    setQuestions([{ question: "", options: ["", "", "", ""], correct_index: 0 }]);
  };

  const addQuestion = () => setQuestions([...questions, { question: "", options: ["", "", "", ""], correct_index: 0 }]);
  const removeQuestion = (qi) => setQuestions(questions.filter((_, i) => i !== qi));
  const updateQ = (qi, field, val) => { const next = [...questions]; next[qi][field] = val; setQuestions(next); };
  const updateOpt = (qi, oi, val) => { const next = [...questions]; next[qi].options[oi] = val; setQuestions(next); };

  const submit = async () => {
    if (!title.trim() || !description.trim() || !rewardLabel.trim()) return toast.error("Champs obligatoires manquants");
    for (const q of questions) {
      if (!q.question.trim()) return toast.error("Toutes les questions doivent être remplies");
      if (q.options.some((o) => !o.trim())) return toast.error("Toutes les options doivent être remplies");
    }
    setBusy(true);
    try {
      await api.post("/quizzes", {
        title, description,
        merchant_id: merchantId === "none" ? null : merchantId,
        reward_label: rewardLabel, reward_percent: parseInt(rewardPercent),
        questions,
      });
      toast.success("Quiz publié.");
      reset(); onClose(); onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cream-50 border-cream-200 rounded-xl">
        <DialogHeader>
          <DialogTitle className="heading-display text-lg text-plum-900">
            <em className="text-pink-500">Nouveau</em> quiz
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-quiz-title" className="rounded-xl border-cream-200 bg-transparent h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Description</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-quiz-desc" className="rounded-xl border-cream-200 bg-transparent" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Commerçant</Label>
              <Select value={merchantId} onValueChange={setMerchantId}>
                <SelectTrigger data-testid="new-quiz-merchant" className="rounded-xl border-cream-200 bg-transparent h-11"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {merchants.map((m) => (<SelectItem key={m.id} value={m.id}>{m.merchant_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">% Réduction</Label>
              <Input type="number" min={1} max={100} value={rewardPercent}
                onChange={(e) => setRewardPercent(e.target.value)} data-testid="new-quiz-percent"
                className="rounded-xl border-cream-200 bg-transparent h-11" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Libellé récompense</Label>
            <Input value={rewardLabel} onChange={(e) => setRewardLabel(e.target.value)}
              placeholder="-10% sur tous les produits" data-testid="new-quiz-reward"
              className="rounded-xl border-cream-200 bg-transparent h-11" />
          </div>

          <div className="space-y-3 pt-3 border-t border-cream-200">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-plum-700/70">Questions</Label>
              <button onClick={addQuestion} className="btn-ghost-line text-xs"><Plus size={12} /> Ajouter</button>
            </div>
            <p className="text-[11px] text-plum-700/50 italic">Les habitants doivent répondre correctement à toutes les questions pour gagner.</p>
            {questions.map((q, qi) => (
              <div key={qi} className="border border-cream-200 p-4 space-y-3 bg-cream-25 rounded-xl">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-plum-700/50">{String(qi + 1).padStart(2, "0")}</span>
                  <Input value={q.question} onChange={(e) => updateQ(qi, "question", e.target.value)}
                    placeholder={`Question ${qi + 1}`} data-testid={`new-quiz-q-${qi}`}
                    className="rounded-xl border-cream-200 bg-transparent h-10" />
                  {questions.length > 1 && (
                    <button onClick={() => removeQuestion(qi)} className="text-plum-700/50 hover:text-pink-500"><X size={14} /></button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 pl-7">
                  {q.options.map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <button
                        type="button" onClick={() => updateQ(qi, "correct_index", oi)}
                        className={`w-4 h-4 rounded-full border shrink-0 transition-colors ${
                          q.correct_index === oi ? "bg-clay-500 border-clay-500" : "border-ink-900/30 hover:border-clay-500"
                        }`}
                        title="Bonne réponse"
                        data-testid={`new-quiz-q${qi}-correct-${oi}`}
                      />
                      <Input value={o} onChange={(e) => updateOpt(qi, oi, e.target.value)}
                        placeholder={String.fromCharCode(65 + oi)} data-testid={`new-quiz-q${qi}-opt${oi}`}
                        className="rounded-xl border-cream-200 bg-transparent h-9" />
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-plum-700/50 italic pl-7">Cliquez sur le rond pour marquer la bonne réponse.</p>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Annuler</Button>
          <Button onClick={submit} disabled={busy} className="btn-pill bg-plum-600 hover:bg-plum-700 text-white" data-testid="new-quiz-submit">
            Publier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
